import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Col, Container, Row, Spinner } from 'react-bootstrap'
import classNames from 'classnames'
import { useDebounce } from 'use-debounce'
import { useOutsideClick } from '../../../common/useOutsideClick'
import styles from './AutocompleteSelectDropdown.module.scss'
import { SelectOption } from '../../SelectDropdown/SelectDropdown'

interface AutocompleteSelectDropdownProps {
  options: SelectOption[]
  initialSelectedOption?: SelectOption | null
  placeholder?: string
  className?: string
  onOptionSelected?: (option: SelectOption) => void
  onInputValueChange?: (inputValue: string) => void
  onQueryChange?: (query: string) => void
  debounceMs?: number
  minSearchChars?: number
  disabled?: boolean
  headerText?: string
  selectedLoading?: boolean
  optionsLoading?: boolean
  onDeselect?: () => void
  hideClearButton?: boolean
  searchInputClassName?: string
  inputName?: string
  inputAutoComplete?: string
}

interface DropdownOptionItemProps {
  optionId: string
  optionLabel: string
  inputValue: string
  onClick: React.MouseEventHandler<HTMLElement>
  disabled?: boolean
}

const DropdownOptionItem: React.FC<DropdownOptionItemProps> = ({ optionLabel, inputValue, onClick, disabled = false }) => {
  return (
    <button
      type='button'
      disabled={disabled}
      onClick={onClick}
      className={classNames('px-2 py-1 w-100 text-start border-0', styles.autocompleteSelectDropdownItemOption, {
        [styles.autocompleteSelectDropdownCustomItemOptionDisabled]: disabled,
      })}
    >
      {highlightMatch(optionLabel, inputValue)}
    </button>
  )
}

interface DropdownMetaItemProps {
  text: string
  className?: string
}

const DropdownMetaItem: React.FC<DropdownMetaItemProps> = ({ text, className = '' }) => {
  return <div className={classNames('px-2 py-1 paragraph-small zocks-text-gray-500', className)}>{text}</div>
}

const highlightMatch = (label: string, match: string): React.ReactNode => {
  const trimmed = match.trim()
  if (!trimmed) {
    return label
  }
  const lowerLabel = label.toLowerCase()
  const lowerMatch = trimmed.toLowerCase()
  const startIndex = lowerLabel.indexOf(lowerMatch)
  if (startIndex === -1) {
    return label
  }
  const endIndex = startIndex + trimmed.length
  return (
    <>
      {label.slice(0, startIndex)}
      <span className='fw-bold'>{label.slice(startIndex, endIndex)}</span>
      {label.slice(endIndex)}
    </>
  )
}

export const AutocompleteSelectDropdown: React.FC<AutocompleteSelectDropdownProps> = ({
  options,
  initialSelectedOption = null,
  placeholder = 'Select Options',
  className,
  onOptionSelected,
  onInputValueChange,
  onQueryChange,
  debounceMs = 400,
  minSearchChars = 0,
  disabled = false,
  headerText,
  selectedLoading = false,
  optionsLoading = false,
  onDeselect,
  hideClearButton = false,
  searchInputClassName = '',
  inputName,
  inputAutoComplete,
}) => {
  const dropdownDivRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState<string>(initialSelectedOption?.label ?? '')
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const lastSelectedIdRef = useRef<string | null>(initialSelectedOption?.id ?? null)

  const normalizedInput = inputValue.trim()
  const [debouncedQuery] = useDebounce(normalizedInput, debounceMs)
  const lastEmittedQueryRef = useRef<string>('')

  const emitQuery = useCallback(
    (nextQuery: string) => {
      if (!onQueryChange) {
        return
      }
      if (lastEmittedQueryRef.current === nextQuery) {
        return
      }
      lastEmittedQueryRef.current = nextQuery
      onQueryChange(nextQuery)
    },
    [onQueryChange],
  )

  useEffect(() => {
    const nextSelectedId = initialSelectedOption?.id ?? null
    if (lastSelectedIdRef.current === nextSelectedId) {
      return
    }
    lastSelectedIdRef.current = nextSelectedId

    if (isEditing || isOpen) {
      return
    }

    const nextLabel = initialSelectedOption?.label ?? ''
    setInputValue(nextLabel)
    onInputValueChange?.(nextLabel)
    emitQuery('')
  }, [emitQuery, initialSelectedOption, isEditing, isOpen, onInputValueChange])

  useEffect(() => {
    if (!isEditing) {
      return
    }

    const nextQuery = debouncedQuery.length >= minSearchChars ? debouncedQuery : ''
    emitQuery(nextQuery)
  }, [debouncedQuery, emitQuery, isEditing, minSearchChars])

  const displayedOptions: SelectOption[] = useMemo(() => {
    if (!isEditing || onQueryChange) {
      return options
    }
    const lowered = inputValue.toLowerCase()
    return options.filter((option) => !!option.label && option.label.toLowerCase().includes(lowered))
  }, [inputValue, isEditing, onQueryChange, options])

  const clearSelection = () => {
    setInputValue('')
    setIsEditing(false)
    onInputValueChange?.('')
    onDeselect?.()
    emitQuery('')
  }

  const restoreSelectionLabel = () => {
    const nextInput = initialSelectedOption?.label ?? ''
    setInputValue(nextInput)
    setIsEditing(false)
    onInputValueChange?.(nextInput)
    emitQuery('')
  }

  const leaveInput = () => {
    setIsOpen(false)
    if (inputValue.trim().length === 0) {
      clearSelection()
      return
    }
    restoreSelectionLabel()
  }

  useOutsideClick(containerRef, () => {
    if (!isOpen) {
      return
    }
    leaveInput()
  })

  const toggleDropdown = () => {
    if (disabled) {
      return
    }

    const nextIsOpen = !isOpen
    setIsOpen(nextIsOpen)

    if (!nextIsOpen) {
      leaveInput()
      return
    }

    queueMicrotask(() => {
      searchInputRef.current?.focus()
    })
  }

  const handleOnOptionSelected = (option: SelectOption) => {
    setIsEditing(false)
    setInputValue(option.label)
    onInputValueChange?.(option.label)
    setIsOpen(false)
    emitQuery('')
    onOptionSelected?.(option)
  }

  const handleInputKey = (evt: React.KeyboardEvent<HTMLElement>) => {
    if (evt.key === 'Escape') {
      evt.preventDefault()
      leaveInput()
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchString = e.currentTarget.value
    setIsEditing(true)
    setInputValue(searchString)
    onInputValueChange?.(searchString)
  }

  const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (isOpen) {
      e.stopPropagation()
    }
  }

  const handleClearOnClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    setIsEditing(true)
    setInputValue('')
    onInputValueChange?.('')
    emitQuery('')
  }

  return (
    <Container ref={containerRef} className='mw-100'>
      <Row>
        <Col className='p-0 w-100 position-relative'>
          <Row>
            <Col className='d-flex gap-1'>
              <div
                className={classNames(
                  'd-flex py-2 zocks-px-0.75 gap-2 align-items-center justify-content-between w-100 bg-white',
                  styles.autocompleteSelectContainer,
                  className,
                  {
                    [styles.autocompleteSelectContainerOpen]: isOpen,
                    [styles.autocompleteSelectContainerDisabled]: disabled,
                  },
                )}
                id='selectDropdown'
                onClick={toggleDropdown}
                role='button'
                tabIndex={disabled ? -1 : 0}
                aria-expanded={isOpen}
                aria-disabled={disabled}
              >
                {selectedLoading ? <Spinner size='sm' /> : null}
                <input
                  ref={searchInputRef}
                  className={classNames(`form-control border-0 p-0 ${searchInputClassName}`, {
                    [styles.autocompleteSelectDropdownInputDisabled]: disabled,
                  })}
                  type='text'
                  value={inputValue}
                  onChange={(e) => handleInputChange(e)}
                  onClick={(e) => handleInputClick(e)}
                  onKeyDown={handleInputKey}
                  placeholder={placeholder}
                  disabled={disabled}
                  {...(inputAutoComplete ? { autoComplete: inputAutoComplete } : {})}
                  {...(inputName ? { name: inputName } : {})}
                />
                {isOpen && inputValue && !hideClearButton ? (
                  <i
                    className={classNames('bi p-0 m-0 bi-x d-flex align-items-center justify-content-center', styles.autocompleteSelectDropdownIcon)}
                    onClick={(e) => handleClearOnClick(e)}
                  />
                ) : null}
                {isOpen ? (
                  <i className={classNames('bi bi-chevron-contract d-flex align-items-center justify-content-center', styles.autocompleteSelectDropdownIcon)} />
                ) : (
                  <i className={classNames('bi bi-chevron-expand d-flex align-items-center justify-content-center', styles.autocompleteSelectDropdownIcon)} />
                )}
              </div>
            </Col>
          </Row>

          <div
            ref={dropdownDivRef}
            className={classNames(
              'dropdown-menu w-100 p-2 border-0 paragraph-small',
              {
                show: isOpen,
              },
              styles.autocompleteSelectDropdownMenu,
            )}
            aria-labelledby='selectDropdown'
          >
            {headerText ? <DropdownMetaItem text={headerText} /> : null}
            <div className={`${styles.autocompleteSelectDropdownOptionsContainer}`}>
              {optionsLoading ? (
                <div className='d-flex align-items-center justify-content-center'>
                  <Spinner size='sm' />
                </div>
              ) : (
                <>
                  {displayedOptions.length ? (
                    displayedOptions.map((option) => (
                      <DropdownOptionItem
                        key={option.id}
                        optionId={option.id}
                        optionLabel={option.label}
                        inputValue={inputValue}
                        onClick={() => handleOnOptionSelected(option)}
                        disabled={option.disabled}
                      />
                    ))
                  ) : (
                    <DropdownMetaItem text='No options' />
                  )}
                </>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  )
}
